import { DateUtils, TFunction } from '@web_opxp/features';
import filter from 'lodash/filter';
import find from 'lodash/find';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import ENV from '../base/env';
import { formatNumberToFixedFraction } from '../base/utils';
import { CelebrationTitles, GLOBAL_ACHIEVEMENTS_LIST, GLOBAL_ACHIEVEMENTS_SUBTYPES_MAP } from './constants';
import {
  EAchievementType,
  EAchievementsIntencity,
  IAchievement,
  IAchievementData,
  IAchievementsData,
  IGlobalAchievement,
  PartialAchievementsData,
  TGlobalAchievements,
} from './types';

function convertAchievementFromApi(achievement: IAchievementData) {
  const converted: IAchievement = {
    ...achievement,
    syncTimestamp: DateUtils.getUnixTimestamp(),
  };
  return converted;
}

export function parseAchievements(fetchedData: PartialAchievementsData): IAchievementsData {
  const single: IAchievement[] = fetchedData.single ? fetchedData.single.map(convertAchievementFromApi) : [];
  const multi: IAchievement[] = fetchedData.multi ? fetchedData.multi.map(convertAchievementFromApi) : [];
  return {
    single,
    multi,
  };
}

export function createAchievement(workoutId: number, type: EAchievementType, subType: number): IAchievement {
  return {
    workoutId,
    type,
    subType,
    timestamp: DateUtils.getUnixTimestamp(),
    syncTimestamp: 0,
  };
}

export function achievementIsGlobal(achievement: IAchievement): achievement is IGlobalAchievement {
  return achievement.workoutId === 0;
}

export const filterGlobalAchievements: (achievements: IAchievement[]) => IGlobalAchievement[] = (achievements) =>
  filter(achievements, achievementIsGlobal);

export const filterByWorkoutId = (achievements: IAchievement[], workoutId: number) =>
  filter(achievements, ['workoutId', workoutId]);

export const mergeAchievements = (oldAchievements: IAchievementsData, newAchievements: IAchievementsData) => {
  const updatedAchievements: IAchievementsData = {
    single: [...oldAchievements.single, ...newAchievements.single],
    multi: [...oldAchievements.multi, ...newAchievements.multi],
  };
  return updatedAchievements;
};

export const filterBySyncTimestamp = (achievements: IAchievement[]) => filter(achievements, ['syncTimestamp', 0]);

const stripCacheTimestamp: (cacheItem: IAchievement) => IAchievementData = (cacheItem) => {
  const now = DateUtils.getUnixTimestamp();
  return {
    workoutId: cacheItem.workoutId,
    type: cacheItem.type,
    subType: cacheItem.subType,
    // change timestamp to relative
    timestamp: Math.min(cacheItem.timestamp - now, -1),
  };
};

export function prepareAchievementsForSaving(cacheData: IAchievementsData): PartialAchievementsData {
  const { single, multi } = cacheData;
  const apiSingle = single.map(stripCacheTimestamp);
  const apiMulti = multi.map(stripCacheTimestamp);
  const apiAll: PartialAchievementsData = {};
  if (apiSingle.length) {
    apiAll.single = apiSingle;
  }
  if (apiMulti.length) {
    apiAll.multi = apiMulti;
  }
  return apiAll;
}

function markAchievementAsSynchronized(achievement: IAchievement) {
  return convertAchievementFromApi(achievement);
}

export function markCacheDataAsSynchronized(cacheData: IAchievementsData): IAchievementsData {
  const single = cacheData.single.map(markAchievementAsSynchronized);
  const multi = cacheData.multi.map(markAchievementAsSynchronized);
  return {
    single,
    multi,
  };
}

export function getRandomTitle(intencity: EAchievementsIntencity) {
  const titles = CelebrationTitles[intencity];
  const randomIndex = Math.floor(Math.random() * titles.length);
  return titles[randomIndex];
}

/**
 * function removes achievements with same type from the list
 * keeping only the highest 'value' i.e. subType
 * Note: achievements will be sorted by type
 * @param achievements all achievements which were detected
 */
export function filterCurrentAchievements(achievements: IAchievement[]) {
  // group achievements by type
  const groups = groupBy(achievements, 'type');
  const result: IAchievement[] = [];
  for (const achievementType in groups) {
    if (Object.hasOwn(groups, achievementType)) {
      const items = groups[achievementType];
      // sort by subType
      const sorted = sortBy(items, 'subType');
      // take latest (biggest)
      const highest = sorted[sorted.length - 1];
      // add to result
      result.push(highest);
    }
  }
  // additionally find a highest level achievement
  const highestLevel = findNewLevelAchievement(achievements, 0);
  if (highestLevel) {
    // and add it to the list if found
    result.push(highestLevel);
  }
  return result;
}

export const findNewLevelAchievement = (achievements: IAchievement[], subType: number) =>
  find(achievements, { type: EAchievementType.NewLevelUnlocked, subType });

export const findHistogramPercentage = (achievements: IAchievement[], subType: number) =>
  find(achievements, { type: EAchievementType.HistogramPercentage, subType });

export const findPlayTimes = (achievements: IAchievement[], subType: number) =>
  find(achievements, { type: EAchievementType.PlayTimes, subType });

export const findWorkoutNumberAchievement = (achievements: IAchievement[], subType: number) =>
  find(achievements, { type: EAchievementType.WorkoutNumber, subType });

export const findPlayTimeLengthAchievement = (achievements: IAchievement[], subType: number) =>
  find(achievements, { type: EAchievementType.PlayTimeLength, subType });

export const findFinishedSessionsAchievement = (achievements: IAchievement[], subType: number) =>
  find(achievements, { type: EAchievementType.FinishedSessions, subType });

export const countGoalReachedAchievements = (achievements: IAchievement[]) =>
  filter(achievements, { type: EAchievementType.GoalReached }).length;

export const findGoalReachedTimesAchievement = (achievements: IAchievement[], subType: number) =>
  find(achievements, { type: EAchievementType.GoalReachedTimes, subType });

export const countAccuracyPerfectAchievements = (achievements: IAchievement[]) =>
  filter(achievements, { type: EAchievementType.AccuracyPerfect }).length;

export const findAccuracyPerfectAggAchievement = (achievements: IAchievement[], subType: number) =>
  find(achievements, { type: EAchievementType.AccuracyPerfectAgg, subType });

export const countHistogramPercentageAchievements = (achievements: IAchievement[], subType: number) =>
  filter(achievements, { type: EAchievementType.HistogramPercentage, subType }).length;

const isHistogramPercentage90pAchievementOfSubtype = (subType: number) => (achievement: IAchievement) =>
  achievement.type === EAchievementType.HistogramPercentage90p && achievement.subType === subType;

export const findHistogramPercentage90pAchievement = (achievements: IAchievement[], subType: number) =>
  find(achievements, isHistogramPercentage90pAchievementOfSubtype(subType));

const isHistogramPercentage50pAchievementOfSubtype = (subType: number) => (achievement: IAchievement) =>
  achievement.type === EAchievementType.HistogramPercentage50p && achievement.subType === subType;

export const findHistogramPercentage50pAchievement = (achievements: IAchievement[], subType: number) =>
  find(achievements, isHistogramPercentage50pAchievementOfSubtype(subType));

export function getAchievementIconSrc(type: EAchievementType, subType: number, suffix = '') {
  if (!suffix) {
    suffix = `${subType}`;
  }
  switch (type) {
    case EAchievementType.GoalReached:
      return `${ENV.AssetsBaseUrl}/feature/achievements/GoalReached.svg`;
    case EAchievementType.AccuracyPerfect:
      return `${ENV.AssetsBaseUrl}/feature/achievements/AccuracyPerfect.svg`;
    case EAchievementType.NewLevelUnlocked:
      if (subType === 0) {
        // for max level we show same icon as for the highscore
        return `${ENV.AssetsBaseUrl}/feature/achievements/HighScore.svg`;
      }
      return `${ENV.AssetsBaseUrl}/feature/achievements/NewLevelUnlocked.svg`;
    case EAchievementType.HistogramPercentage:
      return `${ENV.AssetsBaseUrl}/feature/achievements/HistogramPercentage_${subType}.svg`;
    case EAchievementType.PlayTimes:
      return `${ENV.AssetsBaseUrl}/feature/achievements/PlayTimes_${subType}.svg`;
    case EAchievementType.HighScore:
      return `${ENV.AssetsBaseUrl}/feature/achievements/HighScore.svg`;
    case EAchievementType.WorkoutNumber:
      return `${ENV.AssetsBaseUrl}/feature/achievements/WorkoutNumber_${suffix}.svg`;
    case EAchievementType.PlayTimeLength:
      return `${ENV.AssetsBaseUrl}/feature/achievements/PlayTimeLength_${suffix}.svg`;
    case EAchievementType.FinishedSessions:
      return `${ENV.AssetsBaseUrl}/feature/achievements/FinishedSessions_${suffix}.svg`;
    case EAchievementType.GoalReachedTimes:
      return `${ENV.AssetsBaseUrl}/feature/achievements/GoalReachedTimes_${suffix}.svg`;
    case EAchievementType.AccuracyPerfectAgg:
      return `${ENV.AssetsBaseUrl}/feature/achievements/AccuracyPerfectAgg_${suffix}.svg`;
    case EAchievementType.HistogramPercentage90p:
      return `${ENV.AssetsBaseUrl}/feature/achievements/HistogramPercentage90p_${suffix}.svg`;
    case EAchievementType.HistogramPercentage50p:
      return `${ENV.AssetsBaseUrl}/feature/achievements/HistogramPercentage50p_${suffix}.svg`;
    default:
      return null;
  }
}

export function getAchievementTitle(type: EAchievementType, subType: number, t: TFunction) {
  switch (type) {
    case EAchievementType.GoalReached:
      return t('Achievements.Names.GoalReached');
    case EAchievementType.AccuracyPerfect:
      return t('Achievements.Names.AccuracyPerfect');
    case EAchievementType.NewLevelUnlocked:
      if (subType === 0) {
        // for max level we have a different localization
        return t('Achievements.Names.NewLevelUnlocked_Max');
      }
      // subType = 0-based level in unity, in UI we have +1 shown and we skip level 1
      return t('Achievements.Names.NewLevelUnlocked', { level: subType + 1 });
    case EAchievementType.HistogramPercentage:
      return t('Achievements.Names.HistogramPercentage', { percentage: subType });
    case EAchievementType.PlayTimes:
      if (subType === 1) {
        return t('Achievements.Names.PlayTimes_First');
      }
      return t('Achievements.Names.PlayTimes', { gamePlays: subType });
    case EAchievementType.HighScore:
      return t('Achievements.Names.HighScore');
    case EAchievementType.WorkoutNumber:
      return t(`Achievements.Names.WorkoutNumber_${subType}`);
    case EAchievementType.PlayTimeLength:
      return t(`Achievements.Names.PlayTimeLength_${subType}`);
    case EAchievementType.FinishedSessions:
      return t(`Achievements.Names.FinishedSessions_${subType}`);
    case EAchievementType.GoalReachedTimes:
      return t(`Achievements.Names.GoalReachedTimes_${subType}`);
    case EAchievementType.AccuracyPerfectAgg:
      return t(`Achievements.Names.AccuracyPerfectAgg_${subType}`);
    case EAchievementType.HistogramPercentage90p:
      return t(`Achievements.Names.HistogramPercentage90p_${subType}`);
    case EAchievementType.HistogramPercentage50p:
      return t(`Achievements.Names.HistogramPercentage50p_${subType}`);
    default:
      return null;
  }
}

export function getAchievementDescription(type: EAchievementType, subType: number, t: TFunction) {
  switch (type) {
    case EAchievementType.WorkoutNumber:
      return t(`Achievements.Descriptions.WorkoutNumber_${subType}`);
    case EAchievementType.PlayTimeLength:
      return t(`Achievements.Descriptions.PlayTimeLength_${subType}`);
    case EAchievementType.FinishedSessions:
      return t(`Achievements.Descriptions.FinishedSessions_${subType}`);
    case EAchievementType.GoalReachedTimes:
      return t(`Achievements.Descriptions.GoalReachedTimes_${subType}`);
    case EAchievementType.AccuracyPerfectAgg:
      return t(`Achievements.Descriptions.AccuracyPerfectAgg_${subType}`);
    case EAchievementType.HistogramPercentage90p:
      return t(`Achievements.Descriptions.HistogramPercentage90p_${subType}`);
    case EAchievementType.HistogramPercentage50p:
      return t(`Achievements.Descriptions.HistogramPercentage50p_${subType}`);
    default:
      return null;
  }
}

export function getAchievementProgressFormat(type: EAchievementType, counter: number, goal: number, t: TFunction) {
  switch (type) {
    case EAchievementType.WorkoutNumber:
      return t('Achievements.Progress.WorkoutNumber', { counter, goal });
    case EAchievementType.PlayTimeLength:
      return t('Achievements.Progress.PlayTimeLength', { counter: formatNumberToFixedFraction(counter, 1), goal });
    case EAchievementType.FinishedSessions:
      return t('Achievements.Progress.FinishedSessions', { counter, goal });
    case EAchievementType.GoalReachedTimes:
      return t('Achievements.Progress.GoalReachedTimes', { counter, goal });
    case EAchievementType.AccuracyPerfectAgg:
      return t('Achievements.Progress.AccuracyPerfectAgg', { counter, goal });
    case EAchievementType.HistogramPercentage50p:
      return t('Achievements.Progress.HistogramPercentage50p', { counter, goal });
    case EAchievementType.HistogramPercentage90p:
      return t('Achievements.Progress.HistogramPercentage90p', { counter, goal });
    default:
      return '';
  }
}

export const findMaxTierOfGlobalAchievement = (achievements: IGlobalAchievement[], type: TGlobalAchievements) => {
  const achievementsOfType = sortBy(filter(achievements, { type }), ['subType']);
  return achievementsOfType.length > 0 ? achievementsOfType[achievementsOfType.length - 1] : undefined;
};

export const sortByTierAndType = (achievements: IGlobalAchievement[]) => {
  const result: IGlobalAchievement[] = [];
  // sort by tier defined in constants
  for (let tier = 0; tier < 3; tier++) {
    // inside tier we need to sort by type according to predefined list
    const tierAchievements: IGlobalAchievement[] = GLOBAL_ACHIEVEMENTS_LIST.flatMap((type) => {
      // get current tier subtype from map
      const tierSubType = GLOBAL_ACHIEVEMENTS_SUBTYPES_MAP[type][tier];
      const achievement = find(achievements, { type, subType: tierSubType });
      return achievement ?? [];
    });
    result.push(...tierAchievements);
  }
  return result;
};

export const sortAchievements = (achievements: IAchievement[]) => {
  const localAchievements = filter(achievements, (a) => a.workoutId > 0);
  const globalAchievements = filterGlobalAchievements(achievements);
  // sort global achievements by tier
  const sorted: IGlobalAchievement[] = sortByTierAndType(globalAchievements);
  return [...localAchievements, ...sorted];
};

export const sortByTierAndUniqueType = (achievements: IGlobalAchievement[]) => {
  const sorted = sortByTierAndType(achievements);
  return uniqBy(sorted, 'type');
};
